import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Base from '../Base';
import Card from '../../components/elements/Card';
import Icon from '../../components/common/Icon';
import Image from '../../components/images/Image';
import NotFound from '../../components/common/NotFound';
import { apiRequest, fetchUserInfo, teamPhoto } from '../../utils';
import '../../components/common/Back.css';

const toHHMM = (v) => {
  if (!v) return '';
  const s = String(v);
  const m = s.match(/(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (!m) return s;
  return `${m[1].padStart(2, '0')}:${m[2]}`;
};

const getEventStatus = (startTime, endTime, dayIso) => {
  const now = new Date();
  const today = now.toISOString().split('T')[0];
  if (dayIso !== today) {
    return dayIso < today ? 'past_day' : 'future_day';
  }
  const nowHHMM = now.toTimeString().slice(0, 5);
  const s = toHHMM(startTime);
  const e = toHHMM(endTime);
  if (nowHHMM >= s && nowHHMM < e) return 'ongoing';
  if (nowHHMM > e) return 'past';
  return 'upcoming';
};

/* ----------------------- LIST view ----------------------- */
const TeamList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    fetchUserInfo('/teams', setInfo, setLoading, setError);
  }, []);

  const filteredTeams = useMemo(() => {
    if (!info?.teams) return null;
    const q = query.trim().toLowerCase();
    if (!q) return info.teams;
    return info.teams.filter((team) =>
      (team.team_name || '').toLowerCase().includes(q) ||
      (team.company_name || '').toLowerCase().includes(q)
    );
  }, [info, query]);

  return (
    <Base activeNav="home">
      <div className="pp-page">
        <div className="el-topbar">
          <button
            type="button"
            className="el-back-btn"
            onClick={() => navigate('/home')}
            aria-label="Back"
          >
            <Icon name="angle-left" />
          </button>
          <h1 className="el-page-title">{t('findTeam') || t('teams')}</h1>
        </div>

        <div className="el-search-bar">
          <Icon name="search" className="el-search-icon" />
          <input
            type="text"
            className="el-search-input"
            placeholder={t('searchTeams')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          {query && (
            <button
              type="button"
              className="el-search-clear"
              onClick={() => setQuery('')}
              aria-label="Clear"
            >
              <Icon name="cross" />
            </button>
          )}
        </div>

        {filteredTeams && filteredTeams.length > 0 ? (
          <div className="pp-team-grid">
            {filteredTeams.map((team) => {
              const usingCompanyLogo = !team.team_picture && !!team.company_picture;
              const photo = teamPhoto(team.team_picture, team.company_picture);
              return (
                <Link
                  key={team.team_id}
                  to={`/find-team/${team.team_id}`}
                  className={`pp-team-card ${usingCompanyLogo ? 'el-team-card--logo' : ''}`}
                >
                  <img
                    src={photo}
                    alt=""
                    className={`pp-team-img ${usingCompanyLogo ? 'el-team-img--contain' : ''}`}
                  />
                  <div className="pp-team-fade" />
                  <div className="pp-team-meta">
                    <div className="pp-team-name">{team.team_name}</div>
                    {team.company_name && (
                      <div className="pp-team-company">{team.company_name}</div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="el-empty">
            <NotFound translationKey="noTeamsFound" loading={loading} />
          </div>
        )}
      </div>
    </Base>
  );
};

/* ----------------------- DETAIL view ----------------------- */
const TeamDetail = ({ teamId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest('GET', `/team-locator/${teamId}`);
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [teamId]);

  // Scroll the live activity into view once loaded
  useEffect(() => {
    if (!data?.planning_by_day) return;
    const has = Object.values(data.planning_by_day).flat().length > 0;
    if (!has) return;
    const ongoing = document.querySelector('.plan-item--ongoing');
    if (ongoing) ongoing.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [data]);

  const planningByDay = data?.planning_by_day || {};
  const dayEntries = Object.entries(planningByDay);
  const todayIso = useMemo(() => new Date().toISOString().split('T')[0], []);

  const team = data?.team;

  // Live activity for the "right now" badge in the header
  const liveActivity = useMemo(() => {
    for (const [dayIso, items] of dayEntries) {
      for (const a of items) {
        if (getEventStatus(a.start_time, a.end_time, dayIso) === 'ongoing') {
          return { ...a, dayIso };
        }
      }
    }
    return null;
  }, [dayEntries]);

  return (
    <Base activeNav="home">
      <Card className="schedule-header" mb="2">
        {/* Hand-rolled rather than <Back />, because this one always returns to the team list
            and never to whatever history holds. It wears the shared disc all the same. */}
        <div className="back-slot">
          <button
            type="button"
            className="back-btn"
            onClick={() => navigate('/find-team')}
            aria-label={t('back')}
          >
            <Icon name="angle-left" />
          </button>
        </div>

        {team ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.4rem 0.5rem 0.4rem 2.9rem' }}>
            <Image
              size="3.25rem"
              className="circle"
              url={teamPhoto(team.photo_url, team.company_photo)}
            />
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <div style={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2, color: '#111827' }}>
                {team.name}
              </div>
              {team.company_name && (
                <div style={{ color: '#6b7280', fontSize: '0.82rem' }}>
                  <Icon name="corporate" style={{ fontSize: '11px' }} className="me-1" />
                  {team.company_name}
                </div>
              )}
              {team.house_name && (
                <div
                  style={{
                    fontSize: '0.72rem',
                    fontWeight: 800,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: team.house_color || '#6b7280',
                    marginTop: '0.15rem',
                  }}
                >
                  {team.house_name}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ padding: '0.6rem' }} className="text-center">
            <Icon name="users-alt" className="me-2" theme="secondary" />
            <span className="text-muted">{loading ? '…' : (t('teamNotFound') || 'Team not found')}</span>
          </div>
        )}
      </Card>

      {liveActivity && (
        <Card mb="2" className="mx-3" style={{ background: 'linear-gradient(135deg, #fff4ed 0%, #fff 100%)', border: '1px solid rgba(255, 120, 75, 0.25)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <span className="plan-item__live-dot" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.05em', color: 'var(--primary-color, #ff784b)', textTransform: 'uppercase' }}>
                {t('liveNow') || 'Live now'}
              </div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: '#111827' }}>
                {liveActivity.name}
              </div>
              <div style={{ color: '#6b7280', fontSize: '0.82rem' }}>
                <Icon name="clock" style={{ fontSize: '11px' }} className="me-1" />
                {toHHMM(liveActivity.start_time)} – {toHHMM(liveActivity.end_time)}
              </div>
            </div>
          </div>
        </Card>
      )}

      {dayEntries.length > 0 ? (
        <div className="schedule">
          {dayEntries.map(([dayIso, items]) => {
            const isUnscheduled = dayIso === '1900-01-01';
            const isToday = dayIso === todayIso;
            const isPast = !isUnscheduled && dayIso < todayIso;

            const date = !isUnscheduled ? new Date(dayIso) : null;
            const dayLabel = isUnscheduled
              ? (t('unscheduled') || 'Unscheduled / TBC')
              : date.toLocaleDateString(undefined, { weekday: 'long' });
            const dateLabel = isUnscheduled
              ? ''
              : date.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });

            return (
              <div
                key={dayIso}
                className={`schedule-day ${isToday ? 'schedule-day--today' : ''} ${
                  isPast ? 'schedule-day--past' : ''
                }`}
              >
                <div className="schedule-day__header mx-3">
                  <div className="schedule-day__heading">
                    <Icon name="calendar" className="me-2" theme={isToday ? 'primary' : 'secondary'} />
                    <span className="schedule-day__weekday">{dayLabel}</span>
                    {dateLabel && <span className="schedule-day__date">{dateLabel}</span>}
                  </div>
                  {isToday && <span className="schedule-day__today-pill">{t('today')}</span>}
                </div>

                <div className="schedule-day__items">
                  {items.map((activity, idx) => {
                    const startHHMM = toHHMM(activity.start_time);
                    const endHHMM = toHHMM(activity.end_time);
                    const status = getEventStatus(activity.start_time, activity.end_time, dayIso);
                    const isOngoing = status === 'ongoing';
                    const isPastItem = status === 'past' || status === 'past_day';

                    return (
                      <Card
                        key={`${dayIso}-${activity.id}-${idx}`}
                        p="3"
                        mx="3"
                        mb="1"
                        className={`plan-item ${isOngoing ? 'plan-item--ongoing' : ''} ${
                          isPastItem ? 'plan-item--past' : ''
                        } ${activity.is_challenge ? 'plan-item--challenge' : ''}`}
                      >
                        <div className="plan-item__row">
                          <div className="plan-item__time">
                            <div className="plan-item__time-start">{startHHMM}</div>
                            {endHHMM && <div className="plan-item__time-end">{endHHMM}</div>}
                          </div>
                          <div className="plan-item__divider" aria-hidden="true">
                            <span className="plan-item__dot" />
                          </div>
                          <div className="plan-item__body">
                            <div className="plan-item__name">{activity.name}</div>
                            <div className="plan-item__meta">
                              {isOngoing && (
                                <span className="plan-item__status plan-item__status--live">
                                  <span className="plan-item__live-dot" /> {t('live')}
                                </span>
                              )}
                              {!isOngoing && isPastItem && (
                                <span className="plan-item__status plan-item__status--past">
                                  <Icon name="check" /> {t('done')}
                                </span>
                              )}
                              {!isOngoing && !isPastItem && (
                                <span className="plan-item__status plan-item__status--upcoming">
                                  <Icon name="clock" /> {t('upcoming')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        team && <NotFound translationKey="noScheduleFound" loading={loading} />
      )}
    </Base>
  );
};

/* ----------------------- Page wrapper ----------------------- */
const Page = () => {
  const { teamId } = useParams();
  return teamId ? <TeamDetail teamId={teamId} /> : <TeamList />;
};

export default Page;
